import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MARK_FULL_D, MARK_VIEW_BOX } from './novariMarkPath';

interface BrandIconProps {
  size?: number;
  className?: string;
  variant?: 'default' | 'white' | 'monochrome';
  animate?: boolean;
  isKids?: boolean;
  /** Hide the mark from assistive technology when adjacent text names it. */
  decorative?: boolean;
}

/**
 * The Novari mark, drawn from its vector path and coloured by the
 * `--brand-mark` token, so it follows the active palette and light/dark mode
 * (index.css). It used to be one orange PNG for every theme: in Sky or Focus
 * it clashed with the interface and with the loader, which already used the
 * palette accent. Kids keeps its own artwork.
 */
export const BrandIcon: React.FC<BrandIconProps> = ({
  size = 32,
  className = '',
  variant = 'default',
  animate = true,
  isKids = false,
  decorative = false,
}) => {
  const fill = variant === 'white' ? '#FFFFFF' : variant === 'monochrome' ? 'currentColor' : 'var(--brand-mark, var(--accent-primary))';

  return (
    <motion.div
      className={`relative flex items-center justify-center ${className}`}
      aria-hidden={decorative || undefined}
      initial={animate ? { opacity: 0, scale: 0.8, rotate: -10 } : false}
      animate={animate ? { opacity: 1, scale: 1, rotate: 0 } : false}
      transition={{ duration: 0.5, ease: "easeOut" }}
      style={{ width: size, height: size }}
    >
      {isKids ? (
        <img src="/brand/novari-mark.png?v=3" alt={decorative ? "" : "Novari"} className="h-full w-full object-contain" />
      ) : (
        <svg viewBox={MARK_VIEW_BOX} width={size} height={size} role={decorative ? undefined : "img"} aria-label={decorative ? undefined : "Novari"}>
          <path d={MARK_FULL_D} fill={fill} />
        </svg>
      )}
    </motion.div>
  );
};

/**
 * Keeps the browser-tab icon in the active palette/mode colour. Re-reads
 * `--brand-mark` whenever the palette or dark class on <html> changes.
 */
export function useBrandFavicon() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    let last = '';
    const sync = () => {
      const colour = getComputedStyle(root).getPropertyValue('--brand-mark').trim()
        || getComputedStyle(root).getPropertyValue('--accent-primary').trim();
      if (!colour || colour === last) return;
      last = colour;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${MARK_VIEW_BOX}"><path d="${MARK_FULL_D}" fill="${colour}"/></svg>`;
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"][type="image/svg+xml"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/svg+xml';
        document.head.appendChild(link);
      }
      link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class', 'data-palette', 'data-theme', 'style'] });
    return () => observer.disconnect();
  }, []);
}

export default BrandIcon;
